import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from "react"
import { useAuth } from "@/lib/auth-context"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Plus, Pencil, Trash2 } from "lucide-react"
import { API_BASE } from '@/lib/api-base'
import {
  MASK_MAX_LENGTH,
  cpfCnpjError,
  maskCpfCnpj,
  maskPhone,
  onlyDigits,
  optionalPhoneError,
} from "@/lib/br-masks"
import { formatBRDate } from "@/lib/br-dates"
import { SetupModuleEmptyCta } from "@/components/setup-module-empty-cta"

interface Cliente {
  id: string
  nome: string
  documento: string | null
  telefone: string | null
  endereco: string | null
  status: string
  criado_em: string
}

function ClientesPage() {
  const apiBase = API_BASE
  const { user, apiFetch, isSuperAdmin, selectedTenantId } = useAuth()
  const [clientes, setClientes] = useState<Cliente[]>([])
  const [showForm, setShowForm] = useState(false)
  const [editCliente, setEditCliente] = useState<Cliente | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [form, setForm] = useState({
    nome: "",
    documento: "",
    telefone: "",
    endereco: "",
  })

  useEffect(() => {
    if (!user) return
    if (isSuperAdmin && !selectedTenantId) return
    fetchClientes()
  }, [user, isSuperAdmin, selectedTenantId])

  const fetchClientes = async () => {
    try {
      const res = await apiFetch(`${apiBase}/api/clientes/`)
      if (res.ok) {
        setClientes(await res.json())
      }
    } catch (e) {
      console.error(e)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.nome.trim()) {
      setSubmitError("Nome e obrigatorio.")
      return
    }
    const docErr = cpfCnpjError(form.documento)
    if (docErr) {
      setSubmitError(docErr)
      return
    }
    const telErr = optionalPhoneError(form.telefone, "Telefone")
    if (telErr) {
      setSubmitError(telErr)
      return
    }
    setSubmitError(null)
    const payload = {
      nome: form.nome.trim(),
      documento: onlyDigits(form.documento) || null,
      telefone: onlyDigits(form.telefone) || null,
      endereco: form.endereco.trim() || null,
    }
    try {
      const url = editCliente 
        ? `${apiBase}/api/clientes/${editCliente.id}`
        : `${apiBase}/api/clientes/`
      const res = await apiFetch(url, {
        method: editCliente ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })
      if (res.ok) {
        setShowForm(false)
        setEditCliente(null)
        setForm({ nome: "", documento: "", telefone: "", endereco: "" })
        fetchClientes()
      } else {
        const errData = await res.json().catch(() => ({})) as { detail?: string }
        setSubmitError(typeof errData?.detail === "string" ? errData.detail : "Erro ao salvar.")
      }
    } catch (e) {
      console.error(e)
      setSubmitError("Erro de conexao.")
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm("Tem certeza que deseja excluir este cliente?")) return
    try {
      const res = await apiFetch(`${apiBase}/api/clientes/${id}`, {
        method: "DELETE",
      })
      if (res.ok) {
        fetchClientes()
      } else {
        const errData = await res.json().catch(() => ({})) as { detail?: string }
        alert(errData?.detail || "Erro ao excluir.")
      }
    } catch (e) {
      console.error(e)
      alert("Erro de conexao.")
    }
  }

  const handleToggleStatus = async (cliente: Cliente) => {
    const novoStatus = cliente.status === "ATIVO" ? "INATIVO" : "ATIVO"
    try {
      const res = await apiFetch(`${apiBase}/api/clientes/${cliente.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: novoStatus }),
      })
      if (res.ok) {
        fetchClientes()
      }
    } catch (e) {
      console.error(e)
    }
  }

  const filteredClientes = clientes.filter(c => 
    c.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.documento && c.documento.includes(searchTerm)) ||
    (c.telefone && c.telefone.includes(searchTerm))
  )

  if (isSuperAdmin && !selectedTenantId) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Selecione uma granja no menu lateral.</p>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold">Clientes</h1>
        <Button className="self-start sm:self-auto" onClick={() => {
          setSubmitError(null)
          setShowForm(true)
          setEditCliente(null)
          setForm({ nome: "", documento: "", telefone: "", endereco: "" })
        }}>
          <Plus className="h-4 w-4 mr-2" /> Novo Cliente
        </Button>
      </div>

      <SetupModuleEmptyCta
        module="clientes"
        label="Clientes"
        href="/clientes"
        onAdd={() => {
          setSubmitError(null)
          setShowForm(true)
          setEditCliente(null)
          setForm({ nome: "", documento: "", telefone: "", endereco: "" })
        }}
      />

      <div>
        <Input
          placeholder="Buscar por nome, documento ou telefone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-md"
        />
      </div>

      <Dialog
        open={showForm || !!editCliente}
        onOpenChange={(open) => {
          if (!open) {
            setShowForm(false)
            setEditCliente(null)
            setSubmitError(null)
          }
        }}
      >
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto gap-0 p-0">
          <DialogTitle className="sr-only">
            {editCliente ? 'Editar Cliente' : 'Novo Cliente'}
          </DialogTitle>
          <div className="p-6 space-y-4">
            <h2 className="text-lg font-semibold pr-10">
              {editCliente ? 'Editar Cliente' : 'Novo Cliente'}
            </h2>
            {submitError && (
              <div className="p-3 bg-destructive/10 text-destructive rounded-md text-sm">
                {submitError}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Nome *</Label>
                <Input
                  value={form.nome}
                  onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  maxLength={MASK_MAX_LENGTH.nome}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Documento (CPF/CNPJ)</Label>
                <Input
                  value={form.documento}
                  onChange={(e) => setForm({ ...form, documento: maskCpfCnpj(e.target.value) })}
                  placeholder="000.000.000-00 ou 00.000.000/0000-00"
                  inputMode="numeric"
                  autoComplete="off"
                  maxLength={MASK_MAX_LENGTH.cpfCnpj}
                />
              </div>
              <div className="space-y-2">
                <Label>Telefone</Label>
                <Input
                  value={form.telefone}
                  onChange={(e) => setForm({ ...form, telefone: maskPhone(e.target.value) })}
                  placeholder="(00) 00000-0000"
                  inputMode="numeric"
                  autoComplete="tel"
                  maxLength={MASK_MAX_LENGTH.phone}
                />
              </div>
              <div className="space-y-2">
                <Label>Endereco</Label>
                <textarea
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={form.endereco}
                  onChange={(e) => setForm({ ...form, endereco: e.target.value })}
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit">Salvar</Button>
                <Button variant="outline" type="button" onClick={() => { setShowForm(false); setEditCliente(null); setSubmitError(null) }}>
                  Cancelar
                </Button>
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      <div className="grid gap-4">
        {filteredClientes.map((c) => (
          <Card key={c.id}>
            <CardHeader>
              <CardTitle className="flex justify-between items-center gap-2">
                <span className="text-base">{c.nome}</span>
                <Badge variant={c.status === "ATIVO" ? "default" : "secondary"}>
                  {c.status}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {c.documento && <p className="text-sm text-muted-foreground">Documento: {maskCpfCnpj(c.documento)}</p>}
              {c.telefone && <p className="text-sm text-muted-foreground">Telefone: {maskPhone(c.telefone)}</p>}
              {c.endereco && <p className="text-sm text-muted-foreground">Endereco: {c.endereco}</p>}
              <p className="text-xs text-muted-foreground mt-2">
                Cadastrado em: {formatBRDate(c.criado_em)}
              </p>
              <div className="mt-3 flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setEditCliente(c)
                    setForm({
                      nome: c.nome,
                      documento: c.documento ? maskCpfCnpj(c.documento) : "",
                      telefone: c.telefone ? maskPhone(c.telefone) : "",
                      endereco: c.endereco || "",
                    })
                  }}
                >
                  <Pencil className="h-4 w-4 mr-1" /> Editar
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleToggleStatus(c)}
                >
                  {c.status === "ATIVO" ? "Desativar" : "Ativar"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  aria-label={`Excluir cliente ${c.nome}`}
                  onClick={() => handleDelete(c.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredClientes.length === 0 && (
        <p className="text-muted-foreground text-center py-8">
          {searchTerm ? "Nenhum cliente encontrado." : "Nenhum cliente cadastrado."}
        </p>
      )}
    </div>
  )
}

export const Route = createFileRoute('/_authenticated/clientes')({
  component: ClientesPage,
})
